import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import axios from 'axios';
import Loader from 'src/components/loader';
import { successMsg } from 'src/components/utilities/utils';
interface IProps {
  videoId: string;
  domainId: string;
}

interface User {
  id: string;
  UserName: string;
  Email: string;
  firstname: string;
  lastname: string;
}

interface Permission {
  id: string;
  domainid: string;
  presentationid: string;
  userid: string;
  role: number;
  userName?: string;
  email?: string;
  firstname?: string;
  lastname?: string;
}

const LiveStudioHosts: React.FC<IProps> = ({ videoId, domainId }) => {
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [selectedRole, setSelectedRole] = useState<number>(1);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch
  } = useForm();

  const API_BASE_URL = process.env.REACT_APP_BASE_API_URL;

  useEffect(() => {
    fetchPermissions();
  }, [videoId]);

  const fetchPermissions = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/v2/livemeeting/permissions/${videoId}`);
      if (response?.data?.success) {
        setPermissions(response?.data?.permissions);
      }
    } catch (error: any) {
      console.error('Error fetching permissions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onSearchSubmit = async (data: any) => {
    if (!data.email || data.email.trim() === '') {
      setSearchError('Please enter an email to search');
      return;
    }

    setIsSearching(true);
    setSearchError('');
    setSearchResults([]);

    try {
      const response = await axios.get(`${API_BASE_URL}/v2/livemeeting/users/search`, {
        params: { email: data.email }
      });

      if (response.data.success && response.data.users.length > 0) {
        setSearchResults(response.data.users);
      } else {
        setSearchError('No users found with this email');
      }
    } catch (error: any) {
      console.error('Error searching users:', error);
      setSearchError(
        error.response?.data?.message || 'Error searching for users. Please try again.'
      );
    } finally {
      setIsSearching(false);
    }
  };

  const handleAddHost = async (user: User, role: number) => {
    const existingPermission = permissions.find((p) => p.userid === user.id);
    if (existingPermission) {
      successMsg('This user is already added as a ' + getRoleName(existingPermission.role));
      return;
    }

    const existingHost = permissions.find((p) => p?.role === 1);
    if (existingHost && role === 1) {
      successMsg('Host is already created. Only one host is allowed per live studio.');
      return;
    }

    setIsSaving(true);
    try {
      const response = await axios.post(`${API_BASE_URL}/v2/livemeeting/permissions/save`, {
        domainid: domainId,
        presentationid: videoId,
        userid: user.id,
        role: role
      });

      if (response.data.success) {
        await fetchPermissions();
        setSearchResults([]);
        reset();
        successMsg(`Successfully added ${user.firstname} ${user.lastname} as ${getRoleName(role)}`);
      }
    } catch (error: any) {
      console.error('Error adding host:', error);
      successMsg(error.response?.data?.error || 'Error adding host. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePermission = async (permissionId: string) => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/v2/livemeeting/permissions/${permissionId}`
      );

      if (response.data.success) {
        await fetchPermissions();
        successMsg('Permission removed successfully');
      }
    } catch (error: any) {
      console.error('Error deleting permission:', error);
      successMsg('Error removing permission. Please try again.');
    }
  };

  const getRoleName = (role: number): string => {
    switch (role) {
      case 1:
        return 'Host';
      case 2:
        return 'Co-Host';
      case 3:
        return 'Guest';
      default:
        return 'Unknown';
    }
  };

  const getRoleBadgeClass = (role: number): string => {
    switch (role) {
      case 1:
        return 'bg-blue-100 text-blue-800';
      case 2:
        return 'bg-green-100 text-green-800';
      case 3:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const hostExists = permissions.some((p) => p?.role === 1);

  return (
    <div className="mb-8">
      <label className="font-semibold">Live Studio</label>
      <p className="my-4">
        {`Search and add a host for this video's live studio. Only one host is allowed per live studio.`}
      </p>

      <form onSubmit={handleSubmit(onSearchSubmit)} className="mb-5">
        <div className="flex flex-col gap-3">
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <input
                type="email"
                className="input-style w-full"
                placeholder="Search by email address"
                {...register('email', { required: 'Email is required' })}
              />
              {errors.email && (
                <span className="text-red-500 text-xs mt-1">{errors.email.message as string}</span>
              )}
            </div>
            <button
              type="submit"
              disabled={isSearching}
              className="btn-primary w-auto whitespace-nowrap mt-0 borderonly-btn">
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>

          {searchError && (
            <div className="text-red-500 text-sm bg-red-50 p-3 rounded">{searchError}</div>
          )}
        </div>
      </form>

      {searchResults.length > 0 && (
        <div className="mb-5 border rounded p-4 bg-gray-50">
          <h4 className="font-semibold mb-3">Search Results</h4>
          {searchResults.map((user) => (
            <div
              key={user.id}
              className="flex items-center justify-between bg-white p-3 rounded border mb-2">
              <div>
                <div className="font-medium">
                  {user.firstname} {user.lastname}
                </div>
                <div className="text-sm text-gray-600">{user.Email}</div>
              </div>
              <div className="flex items-center gap-2">
                <select
                  className="input-style w-32 py-1 text-sm"
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(Number(e.target.value))}>
                  <option value={1}>Host</option>
                  <option value={2}>Co-Host</option>
                </select>
                <button
                  onClick={() => handleAddHost(user, selectedRole)}
                  disabled={isSaving}
                  className="btn-primary w-auto whitespace-nowrap mt-0 text-sm px-3 py-1">
                  {isSaving ? 'Adding...' : 'Add'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-5">
        <h4 className="font-semibold mb-3">Current Live Studio Permissions</h4>
        <table className="table-bordered w-full">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={4}>
                  <Loader />
                </td>
              </tr>
            ) : permissions.length > 0 ? (
              permissions.map((permission) => (
                <tr key={permission.id}>
                  <td>
                    {permission.firstname || ''} {permission.lastname || ''}
                  </td>
                  <td>{permission.email || permission.userName || '-'}</td>
                  <td>
                    <span
                      className={`px-2 py-1 rounded text-xs font-medium ${getRoleBadgeClass(
                        permission.role
                      )}`}>
                      {getRoleName(permission.role)}
                    </span>
                  </td>
                  <td
                    className="cursor-pointer text-red-600 hover:text-red-800"
                    onClick={() => handleDeletePermission(permission.id)}>
                    Remove
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="text-center py-4 text-gray-500">
                  No host added yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default LiveStudioHosts;
